import {
  ExclamationCircleFilled, PlusOutlined, EditOutlined, DeleteOutlined,
  PercentageOutlined, BankOutlined, TeamOutlined, AppstoreOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  Button, Modal as AntdModal, Table, Modal, Form, InputNumber, Select, Space,
  Tag, Card, Row, Col, Typography, Tooltip, Empty,
} from 'antd';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import AddTauxCommission from './AddTauxCommission';
import ActionDropdown from '../../production/components/ActionDropdown';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { SelectionBlock } from './TauxCommissions';

const { confirm } = AntdModal;
const { Text, Title } = Typography;

const PRIMARY = '#242166';
const GRADIENT = 'linear-gradient(135deg, #242166 0%, #1890ff 100%)';
const INTERMEDIAIRE_TYPES = ['COURTIER', 'AGENT_GENERAL', 'APPORTEUR_REGULIER', 'APPORTEUR_OCCASIONNEL'];

const TYPE_META = {
  COURTIER: { label: 'Courtier', color: 'geekblue', icon: <TeamOutlined /> },
  AGENT_GENERAL: { label: 'Agent général', color: 'purple', icon: <BankOutlined /> },
  APPORTEUR_REGULIER: { label: 'Apporteur régulier', color: 'cyan', icon: <TeamOutlined /> },
  APPORTEUR_OCCASIONNEL: { label: 'Apporteur occasionnel', color: 'gold', icon: <TeamOutlined /> },
};
const typeTag = (t) => {
  const m = TYPE_META[t] || { label: t || 'N/A', color: 'default', icon: <TeamOutlined /> };
  return <Tag color={m.color} icon={m.icon} style={{ borderRadius: 6 }}>{m.label}</Tag>;
};

const TauxCommissionsV2 = () => {
  // ── State (identique à l'écran v1) ──────────────────────────────────────────
  const [tauxCommissions, setTauxCommissions] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTauxCommission, setSelectedTauxCommission] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [initialModalData, setInitialModalData] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [typeIntermediaire, setTypeIntermediaire] = useState('');

  const [isAgencyModalVisible, setIsAgencyModalVisible] = useState(false);
  const [pendingCommissionData, setPendingCommissionData] = useState(null);

  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [agencySelectionType, setAgencySelectionType] = useState('all');
  const [selectedAgencies, setSelectedAgencies] = useState([]);

  const [intermediaires, setIntermediaires] = useState([]);
  const [loadingIntermediaires, setLoadingIntermediaires] = useState(false);
  const [intermediaireSelectionType, setIntermediaireSelectionType] = useState('all');
  const [selectedIntermediaires, setSelectedIntermediaires] = useState([]);

  const [form] = Form.useForm();
  const [bulkTaux, setBulkTaux] = useState(null);

  const colors = { primary: PRIMARY, secondary: '#1890ff', success: '#52c41a', error: '#ff4d4f', gradient: GRADIENT };

  // ── Helpers (identiques) ────────────────────────────────────────────────────
  const hasTauxChanged = (currentData, initialData) => {
    if (!initialData || initialData.length === 0) return true;
    return currentData.some((current, index) => current.taux !== initialData[index]?.taux);
  };
  const parseDate = (d) => (d ? new Date(d).getTime() : 0);
  const sortByNewest = (arr) =>
    [...(arr || [])].sort((a, b) => parseDate(b?.updatedAt ?? b?.createdAt) - parseDate(a?.updatedAt ?? a?.createdAt));
  const logAxiosError = (err, label) => { console.group(` ${label}`); console.error('message:', err); };

  // ── Fetchers (endpoints identiques) ─────────────────────────────────────────
  const fetchAgenciesByType = async (typeInter) => {
    setLoadingAgencies(true);
    try {
      const response = await axiosInstance.get(`/api/agence/intermediaire/${typeInter}`);
      setAgencies(response.data || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des agences:', err);
      toast.error('Erreur lors de la récupération des agences');
      setAgencies([]);
    } finally { setLoadingAgencies(false); }
  };

  const fetchIntermediairesByType = async (typeInter) => {
    setLoadingIntermediaires(true);
    try {
      const response = await axiosInstance.get(`/api/intermediaire?type=${typeInter}`);
      const data = (response.data || []).filter(i => !typeInter || i.type === typeInter || i.typeIntermediaire === typeInter);
      setIntermediaires(data);
    } catch (err) {
      console.error('Erreur lors de la récupération des intermédiaires:', err);
      toast.error('Erreur lors de la récupération des intermédiaires');
      setIntermediaires([]);
    } finally { setLoadingIntermediaires(false); }
  };

  const fetchTauxCommissions = useCallback(() => {
    setLoadingTable(true);
    axiosInstance.get(`/api/taux-commission`)
      .then((res) => setTauxCommissions(sortByNewest(res.data || [])))
      .catch((err) => logAxiosError(err, 'GET /api/taux-commission'))
      .finally(() => setLoadingTable(false));
  }, []);

  useEffect(() => {
    fetchTauxCommissions();
    axiosInstance.get(`/api/categorie`).then((res) => {
      setAllCategories(res.data?.filter((c) => !c.codeCategorie?.startsWith('V')));
    }).catch((err) => logAxiosError(err, 'GET /api/categorie'));
  }, [fetchTauxCommissions]);

  // ── handleSave (identique) ──────────────────────────────────────────────────
  const handleSave = () => {
    form.validateFields()
      .then(values => {
        const updatedModalData = modalData.map(item => ({
          ...item,
          taux: values[item.categorie.codeCategorie],
          organisme: LOGGED_USER?.profil?.organisme?.code,
          typeIntermediaire: editMode ? selectedTauxCommission.typeIntermediaire : typeIntermediaire,
        }));

        if (!hasTauxChanged(updatedModalData, initialModalData)) {
          toast.info('Aucune modification détectée');
          setIsModalVisible(false);
          return;
        }

        const stc = {
          intermediaireId: editMode ? selectedTauxCommission.typeIntermediaire : typeIntermediaire,
          organismeCode: editMode ? selectedTauxCommission.organisme?.code : LOGGED_USER?.profil?.organisme?.code,
          commissions: updatedModalData,
        };

        setPendingCommissionData(stc);
        setIsModalVisible(false);

        const typeInter = selectedTauxCommission?.typeIntermediaire || typeIntermediaire;
        Promise.all([fetchAgenciesByType(typeInter), fetchIntermediairesByType(typeInter)])
          .then(() => setIsAgencyModalVisible(true));
      })
      .catch(error => console.error('Erreur validation formulaire :', error));
  };

  // ── Confirmation propagation (identique) ────────────────────────────────────
  const handleAgencySelectionConfirm = () => {
    const agenciesToApply = agencySelectionType === 'all' ? agencies.map(a => a.codeAgence || a.codeId)
      : agencySelectionType === 'custom' ? selectedAgencies : [];
    const intermediairesToApply = intermediaireSelectionType === 'all' ? intermediaires.map(i => i.codeIntermediaire || i.codeId)
      : intermediaireSelectionType === 'custom' ? selectedIntermediaires : [];

    const finalData = {
      ...pendingCommissionData,
      agencyCodes: agenciesToApply,
      intermediaireCodes: intermediairesToApply,
      intermediaireId: selectedTauxCommission?.typeIntermediaire || typeIntermediaire,
    };

    axiosInstance.put(`/api/taux-commission/parametered/taux-agences`, finalData)
      .then(() => {
        const parts = [];
        if (agenciesToApply.length > 0) parts.push(`${agenciesToApply.length} agence(s)`);
        if (intermediairesToApply.length > 0) parts.push(`${intermediairesToApply.length} intermédiaire(s)`);
        const detail = parts.length > 0 ? ` pour ${parts.join(' et ')}` : '';
        toast.success(`Taux de commission mis à jour avec succès${detail}`);
        fetchTauxCommissions();
        resetPropagationModal();
      })
      .catch(err => { console.error(err); toast.error('Erreur lors de la mise à jour des taux de commission'); resetPropagationModal(); });
  };

  const resetPropagationModal = () => {
    setIsAgencyModalVisible(false);
    setAgencySelectionType('all');
    setSelectedAgencies([]);
    setIntermediaireSelectionType('all');
    setSelectedIntermediaires([]);
    setPendingCommissionData(null);
  };

  const handleCancel = () => setIsModalVisible(false);

  // ── CRUD (identique) ────────────────────────────────────────────────────────
  const handleShow = (edit = false, taux = null) => { setEditMode(edit); setSelectedTauxCommission(taux); setShowModal(true); };
  const handleClose = (savedItem) => { setShowModal(false); setEditMode(false); setSelectedTauxCommission(null); if (savedItem) fetchTauxCommissions(); };

  const showDeleteConfirm = (codeId) => {
    if (!codeId) { toast.error('codeId introuvable'); return; }
    confirm({
      title: 'Êtes-vous sûr de vouloir supprimer ce taux ?',
      icon: <ExclamationCircleFilled />, content: 'Cette action est irréversible.',
      okText: 'Oui', okType: 'danger', cancelText: 'Annuler',
      onOk() { deleteTauxCommission(codeId); },
    });
  };
  const deleteTauxCommission = (codeId) => {
    axiosInstance.delete(`/api/taux-commission/${codeId}`)
      .then(() => { toast.success('Suppression réussie'); fetchTauxCommissions(); })
      .catch(err => { logAxiosError(err, `DELETE /api/taux-commission/${codeId}`); toast.error('Erreur lors de la suppression'); });
  };

  // ── Groupement (identique) ──────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const groupedData = {};
    tauxCommissions.forEach(item => {
      const key = `${item.typeIntermediaire}-${item?.organisme?.code ?? ''}`;
      if (!groupedData[key]) {
        groupedData[key] = { key, codeId: item.codeId, typeIntermediaire: item.typeIntermediaire, organisme: item.organisme, details: [] };
      }
      groupedData[key].details.push(item);
    });
    return Object.values(groupedData);
  }, [tauxCommissions]);

  // ── Édition/ajout (identique) ───────────────────────────────────────────────
  const handleEditClick = (record) => {
    setSelectedTauxCommission(record);
    const existingRatesMap = new Map(record.details.map(item => [item.categorie.codeCategorie, item]));
    const md = allCategories.map(cat => {
      const existingRate = existingRatesMap.get(cat.codeCategorie);
      return {
        codeId: existingRate?.codeId || `${record.key}-${cat.codeCategorie}`,
        categorie: cat,
        taux: existingRate?.taux ?? 0,
        typeIntermediaire: record.typeIntermediaire,
        organisme: record.organisme,
      };
    });
    setModalData(md);
    setInitialModalData(JSON.parse(JSON.stringify(md)));
    setIsModalVisible(true);
  };

  const handleAddClick = () => {
    setSelectedTauxCommission({});
    const md = allCategories.map(cat => ({
      codeId: cat?.categorieId || `$${cat.codeCategorie}`,
      categorie: cat,
      taux: 0,
      typeIntermediaire: typeIntermediaire,
      organisme: LOGGED_USER?.profil?.organisme?.code,
    }));
    setModalData(md);
    setInitialModalData([]);
    setIsModalVisible(true);
  };

  useEffect(() => {
    if (modalData?.length > 0) {
      form.resetFields();
      form.setFieldsValue(Object.fromEntries(modalData.map(item => [item.categorie.codeCategorie, item.taux])));
    }
  }, [modalData, form]);

  const applyBulkTaux = () => {
    if (bulkTaux == null) return;
    form.setFieldsValue(Object.fromEntries(modalData.map(item => [item.categorie.codeCategorie, bulkTaux])));
  };

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const allTaux = tauxCommissions.map(t => Number(t.taux)).filter(n => !Number.isNaN(n));
    const moyenne = allTaux.length ? (allTaux.reduce((s, n) => s + n, 0) / allTaux.length) : 0;
    const compagnies = new Set(grouped.map(g => g.organisme?.code).filter(Boolean));
    return { baremes: grouped.length, categories: allCategories.length, moyenne: moyenne.toFixed(1), compagnies: compagnies.size };
  }, [tauxCommissions, grouped, allCategories]);

  // ── Aperçu des taux (tags) ──────────────────────────────────────────────────
  const renderApercu = (record) => {
    const details = (record.details || []).filter(d => Number(d.taux) > 0);
    if (!details.length) return <Text type="secondary" style={{ fontSize: 12 }}>Aucun taux renseigné</Text>;
    const shown = details.slice(0, 4);
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
        {shown.map((d) => (
          <Tag key={d.codeId} style={{ borderRadius: 6, margin: 0 }} color="blue">
            {(d.categorie?.nom || d.categorie?.libelle || d.categorie?.codeCategorie)} · {d.taux}%
          </Tag>
        ))}
        {details.length > 4 && <Tag style={{ borderRadius: 6, margin: 0 }}>+{details.length - 4}</Tag>}
      </span>
    );
  };

  const columns = [
    { title: "Type d'intermédiaire", dataIndex: 'typeIntermediaire', key: 'typeIntermediaire', width: 200, render: (v) => typeTag(v),
      sorter: (a, b) => (a.typeIntermediaire || '').localeCompare(b.typeIntermediaire || '') },
    { title: 'Compagnie', dataIndex: 'organisme', key: 'organisme', width: 160, render: (org) => <Text strong>{org?.raisonSociale ?? 'N/A'}</Text>,
      sorter: (a, b) => (a.organisme?.raisonSociale || '').localeCompare(b.organisme?.raisonSociale || '') },
    { title: 'Aperçu des taux', key: 'apercu', render: (_, r) => renderApercu(r) },
    { title: 'Actions', key: 'action', width: 110, align: 'center', render: (_, record) => (
        <Tooltip title="Éditer les taux">
          <Button type="primary" ghost size="small" icon={<EditOutlined />} onClick={() => handleEditClick(record)}>Éditer</Button>
        </Tooltip>
      ) },
  ];

  const expandedRowColumns = [
    { title: 'Catégorie', dataIndex: 'categorie', key: 'categorie', render: (cat) => <Tag color="purple" style={{ borderRadius: 6 }}>{cat?.nom ?? cat?.libelle ?? 'N/A'}</Tag> },
    { title: 'Taux', dataIndex: 'taux', key: 'taux', render: (value) => <Text strong style={{ color: PRIMARY }}>{value ?? 0} %</Text> },
    { title: 'Actions', key: 'action', align: 'right', render: (_, record) => (
        <ActionDropdown actions={[
          { key: 'edit', label: 'Modifier', icon: <EditOutlined />, onClick: () => handleShow(true, record) },
          { key: 'delete', label: 'Supprimer', icon: <DeleteOutlined />, danger: true, onClick: () => showDeleteConfirm(record?.codeId) },
        ]} />
      ) },
  ];

  const propagationSummary = useMemo(() => {
    const agCount = agencySelectionType === 'all' ? agencies.length : agencySelectionType === 'custom' ? selectedAgencies.length : 0;
    const intCount = intermediaireSelectionType === 'all' ? intermediaires.length : intermediaireSelectionType === 'custom' ? selectedIntermediaires.length : 0;
    return { agCount, intCount, total: agCount + intCount };
  }, [agencySelectionType, agencies, selectedAgencies, intermediaireSelectionType, intermediaires, selectedIntermediaires]);

  const KpiCard = ({ icon, label, value, suffix }) => (
    <Card size="small" bordered={false} style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 40, height: 40, borderRadius: 10, background: `${PRIMARY}14`, color: PRIMARY, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: PRIMARY }}>{value}{suffix ? <span style={{ fontSize: 13, fontWeight: 500, color: '#9ca3af' }}> {suffix}</span> : null}</div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={'TARIFICATION'} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* En-tête */}
              <div style={{ background: GRADIENT, borderRadius: 14, padding: '18px 22px', marginBottom: 20, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <Title level={4} style={{ margin: 0, color: '#fff' }}><PercentageOutlined style={{ marginRight: 8 }} />Taux de commissions</Title>
                  <Text style={{ color: 'rgba(255,255,255,0.85)' }}>Barèmes de commission par type d'intermédiaire et compagnie</Text>
                </div>
                <Space>
                  <Tooltip title="Rafraîchir"><Button ghost icon={<ReloadOutlined />} onClick={fetchTauxCommissions} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }} /></Tooltip>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClick} style={{ background: '#fff', color: PRIMARY, fontWeight: 600 }}>Nouveau barème</Button>
                </Space>
              </div>

              {/* KPIs */}
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}><KpiCard icon={<AppstoreOutlined />} label="Barèmes" value={kpis.baremes} /></Col>
                <Col xs={12} md={6}><KpiCard icon={<BankOutlined />} label="Catégories couvertes" value={kpis.categories} /></Col>
                <Col xs={12} md={6}><KpiCard icon={<PercentageOutlined />} label="Taux moyen" value={kpis.moyenne} suffix="%" /></Col>
                <Col xs={12} md={6}><KpiCard icon={<TeamOutlined />} label="Compagnies" value={kpis.compagnies} /></Col>
              </Row>

              {/* Table */}
              <Card bordered={false} style={{ borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <Table
                  columns={columns}
                  dataSource={grouped}
                  loading={loadingTable}
                  rowKey={(r) => r.key}
                  pagination={{ showSizeChanger: true, pageSize: 10 }}
                  locale={{ emptyText: <Empty description="Aucun barème de commission" /> }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Table columns={expandedRowColumns} dataSource={record.details} pagination={false} rowKey={(r) => r?.codeId ?? r?.__localId} size="small" />
                    ),
                    rowExpandable: (record) => record.details.length > 0,
                  }}
                />
              </Card>

              {/* Modal édition/ajout */}
              <Modal
                title={selectedTauxCommission?.typeIntermediaire
                  ? `Édition des taux — ${TYPE_META[modalData[0]?.typeIntermediaire]?.label || modalData[0]?.typeIntermediaire} (${modalData[0]?.organisme?.raisonSociale || ''})`
                  : 'Définir des taux de commission'}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={handleCancel}
                okText="Suivant ›"
                cancelText="Annuler"
                width={820}
                zIndex={2000}
                okButtonProps={{ style: { background: PRIMARY } }}
              >
                <Form form={form} layout="vertical">
                  {!selectedTauxCommission?.typeIntermediaire && (
                    <Form.Item label="Type d'intermédiaire" required>
                      <Select
                        value={typeIntermediaire || undefined}
                        onChange={setTypeIntermediaire}
                        options={INTERMEDIAIRE_TYPES.map((inter, idx) => ({ label: TYPE_META[inter]?.label || inter, value: inter, key: idx }))}
                        style={{ width: '100%' }}
                        placeholder="Sélectionner le type d'intermédiaire"
                      />
                    </Form.Item>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, background: '#f6f7fb', padding: '8px 12px', borderRadius: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Raccourci :</Text>
                    <InputNumber min={0} max={100} value={bulkTaux} onChange={setBulkTaux} addonAfter="%" size="small" style={{ width: 120 }} placeholder="Tout à…" />
                    <Button size="small" onClick={applyBulkTaux} disabled={bulkTaux == null}>Appliquer à toutes les catégories</Button>
                  </div>

                  <Table
                    dataSource={modalData}
                    pagination={false}
                    rowKey={record => record.categorie.codeCategorie}
                    size="small"
                    columns={[
                      { title: 'Catégorie', dataIndex: 'categorie', key: 'categorie', render: (cat) => <Tag color="purple" style={{ borderRadius: 6 }}>{cat?.nom ?? cat?.libelle ?? 'N/A'}</Tag> },
                      { title: 'Taux (%)', dataIndex: 'taux', key: 'taux', width: 160, render: (value, record) => (
                          <Form.Item name={record.categorie.codeCategorie} style={{ margin: 0 }} rules={[{ required: true, message: 'Le taux est requis' }]}>
                            <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
                          </Form.Item>
                        ) },
                    ]}
                  />
                </Form>
              </Modal>

              {/* Modal propagation */}
              <Modal
                title={
                  <div style={{ borderBottom: `2px solid ${PRIMARY}`, paddingBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: PRIMARY }}>📡 Propager les nouveaux taux</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      Choisissez à qui appliquer les modifications pour le type{' '}
                      <strong>{TYPE_META[selectedTauxCommission?.typeIntermediaire || typeIntermediaire]?.label || selectedTauxCommission?.typeIntermediaire || typeIntermediaire}</strong>
                    </div>
                  </div>
                }
                open={isAgencyModalVisible}
                onOk={handleAgencySelectionConfirm}
                onCancel={resetPropagationModal}
                okText={propagationSummary.total > 0 ? `Confirmer (${propagationSummary.total} destinataire${propagationSummary.total > 1 ? 's' : ''})` : 'Confirmer sans propagation'}
                cancelText="Annuler"
                width={780}
                zIndex={2100}
                okButtonProps={{ style: { background: GRADIENT, border: 'none', borderRadius: 6 } }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  {propagationSummary.total > 0 ? (
                    <div style={{ background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)', border: '1px solid #1890ff', borderRadius: 8, padding: '10px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>✅</span>
                      <span>Les taux seront appliqués à{' '}
                        {propagationSummary.agCount > 0 && <strong>{propagationSummary.agCount} agence{propagationSummary.agCount > 1 ? 's' : ''}</strong>}
                        {propagationSummary.agCount > 0 && propagationSummary.intCount > 0 && ' et '}
                        {propagationSummary.intCount > 0 && <strong>{propagationSummary.intCount} intermédiaire{propagationSummary.intCount > 1 ? 's' : ''}</strong>}.
                      </span>
                    </div>
                  ) : (
                    <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '10px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>⚠️</span><span>Les taux seront mis à jour sans être propagés.</span>
                    </div>
                  )}

                  <SelectionBlock
                    title="Agences" icon="🏢"
                    selectionType={agencySelectionType}
                    onChangeType={(v) => { setAgencySelectionType(v); setSelectedAgencies([]); }}
                    items={agencies} loading={loadingAgencies}
                    selectedItems={selectedAgencies} onChangeItems={setSelectedAgencies}
                    itemKey="codeAgence"
                    itemLabel={(a) => a.nom || a.raisonSociale || 'Sans nom'}
                    itemSub={(a) => `Code : ${a.codeAgence || a.codeId}`}
                    previewLabel={`agence${agencies.length > 1 ? 's' : ''}`}
                    colors={colors}
                  />

                  <SelectionBlock
                    title="Intermédiaires" icon="👤"
                    selectionType={intermediaireSelectionType}
                    onChangeType={(v) => { setIntermediaireSelectionType(v); setSelectedIntermediaires([]); }}
                    items={intermediaires} loading={loadingIntermediaires}
                    selectedItems={selectedIntermediaires} onChangeItems={setSelectedIntermediaires}
                    itemKey="codeIntermediaire"
                    itemLabel={(i) => `${i.personne?.nom ?? ''} ${i.personne?.prenom ?? ''}`.trim() || i.codeIntermediaire}
                    itemSub={(i) => `Code : ${i.codeIntermediaire} · ${i.type || ''}`}
                    previewLabel={`intermédiaire${intermediaires.length > 1 ? 's' : ''}`}
                    colors={colors}
                  />
                </Space>
              </Modal>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddTauxCommission
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedTauxCommission={selectedTauxCommission}
        tauxCommissions={tauxCommissions}
        onRefetch={fetchTauxCommissions}
      />
    </div>
  );
};

export default TauxCommissionsV2;
